import React, { useEffect, useState } from "react";
import { useBooking } from "../hooks/useBooking";
import FooterStepButton from "../components/FooterStepButton";
import Container from "../components/Container";
import NavigationHeader from "../components/NavigationHeader";
import BookingStepList from "../components/booking/steps/BookingStepList";
import ServiceAddonsCard from "../components/booking/product/ServiceAddonsCard";
import { useProductsSearchSort } from "../hooks/useProductsSearchSort";
import { useStepManager } from "../hooks/useStepManager";
import { useTimeSlots } from "../lib/react-query/useTimeSlots";
import { buildProductsForRequest } from "../store/slices/bookingSlice";

const Addons = () => {
  const {selectedServices, handleAddonSelect, updateAddon, addDefaultAddons, isExclusiveAddonNoteShown, markExclusiveAddonNoteShown, selectedProvider, selectedDate, selectedTime, timeValidatedProducts, setSelectedTime } = useBooking();
  const { navigateToNextStep } = useStepManager();
  const { debouncedSearch, orderField, orderDirection } = useProductsSearchSort();
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());

  const hasAvailableAddons = selectedServices.some((service) => service.has_available_addons);

  useEffect(() => {
    if (!hasAvailableAddons) {
      navigateToNextStep({ replace: true });
    }
  }, [hasAvailableAddons]);

  const productIds = selectedServices.flatMap((s) => [...(s.addons ?? []), ...(s.products ?? [])].map((p) => p.id));
  const hasChangedSinceValidated =
    !!selectedTime &&
    JSON.stringify(buildProductsForRequest(selectedServices)) !== JSON.stringify(timeValidatedProducts);

  const { data: recheckSlots, isFetched: recheckSlotsFetched } = useTimeSlots(
    {
      count: selectedServices.find((s) => s.group_booking_count)?.group_booking_count || 1,
      provider_id: selectedProvider?.id === "any" ? null : selectedProvider?.id,
      "service_id[]": selectedServices.map((s) => s.id),
      date_from: selectedDate,
      date_to: selectedDate,
      ...(productIds.length > 0 && { "product_ids[]": productIds }),
    },
    { enabled: hasChangedSinceValidated && !!selectedDate && !!selectedProvider }
  );

  useEffect(() => {
    if (!hasChangedSinceValidated || !recheckSlotsFetched) return;

    if (recheckSlots?.some((slot) => slot.time === selectedTime)) {
      setSelectedTime(selectedTime);
    } else {
      setSelectedTime(null);
    }
  }, [recheckSlots, recheckSlotsFetched]);

  if (!hasAvailableAddons) return null;

  const toggleExpanded = (serviceId) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  };

  return (
    <Container>
      <NavigationHeader location back="step" />
      <BookingStepList title="Add extras to your services" subtitle="Pick optional add-ons for each service below. Some add extra time, some are included. You can skip this step">
        {selectedServices.map((service) => (
          <ServiceAddonsCard
            key={service.id}
            service={service}
            isExpanded={!collapsedIds.has(service.id)}
            onToggle={() => toggleExpanded(service.id)}
            search={debouncedSearch}
            orderField={orderField}
            orderDirection={orderDirection}
            handleProductSelect={handleAddonSelect}
            updateProduct={updateAddon}
            addDefaultAddons={addDefaultAddons}
            isExclusiveNoteShown={isExclusiveAddonNoteShown}
            onExclusiveNoteShown={markExclusiveAddonNoteShown}
          />
        ))}
      </BookingStepList>
      <FooterStepButton services={selectedServices} loading={hasChangedSinceValidated && !recheckSlotsFetched} />
    </Container>
  );
};

export default Addons;